import { debounce } from '@/components/utils/debounce'
import { auth } from '@/components/utils/firebase'
import { businessDB } from '@shared/database'
import { onAuthStateChanged } from 'firebase/auth'

export const state = () => ({
  hasSetupCompleted: false,
  userDisplayName: null,
  userAuthEmail: null,
  isAuthenticating: true,
  isLoggedIn: false,
  isSigningOut: false,
  uid: null,
  companyId: null,
  isAdmin: false,
  permission: null,
  subscriptionEnder: null,
  isAnonymous: null,
  updateOrdersUnsubscribe: null,
  updateMessageRoomsUnsubscribe: null,
  updateMessagesUnsubscribe: null,
  updateCompanyDepositUnsubscribe: null,
  currentPoint: 0,
  availablePoint: 0,
  messageRooms: [],
  updateMessageRoomsSnapUnsubscribe: null,
  joinedMessageRoomsUnsubscribers: {},
  messageRoomsCache: {},
  messageRoomsUnsubscribers: {},
  totalUnreadCount: 0,
  infoUnreadCount: 0,
  // 最新メッセージルーム監視用
  latestMessageRoomUnsubscribe: null
})

export const mutations = {
  updateUid(state, value) {
    state.uid = value
  },
  updateUserDisplayName(state, value) {
    state.userDisplayName = value
  },
  updateIsAuthenticating(state, value) {
    state.isAuthenticating = value
  },
  updateIsLoggedIn(state, value) {
    state.isLoggedIn = value
  },
  updateIsSigningOut(state, value) {
    state.isSigningOut = value
  },
  async updateAuthStatus(state, user) {
    if (user && !state.isLoggedIn) {
      try {
        debounce(this, saveSignInTime(user), 10000)
      } catch (error) {
        console.log(error)
      }
    }

    if (user) {
      const result = await auth.currentUser.getIdTokenResult(false)
      const claims = result.claims ? result.claims : null
      if (claims) {
        state.companyId = claims.businessCompanyId
        state.isAdmin = claims.businessPermission === 'admin'
        state.permission = claims.businessPermission
      } else {
        state.companyId = null
        state.isAdmin = false
      }
    }

    const isLoggedIn = !!user
    const hasAuthEmail = !!user && !!user.email
    state.userDisplayName = isLoggedIn ? user.displayName : null
    state.uid = isLoggedIn ? user.uid : null
    state.userAuthEmail = hasAuthEmail ? user.email : null
    state.isAuthenticating = false
    state.isLoggedIn = isLoggedIn
    state.isSigningOut = false
    state.isAnonymous = user ? user.isAnonymous : null
    state.hasSetupCompleted = true
  },
  resetState(state) {
    state.uid = null
    state.companyId = null
    state.userDisplayName = null
    state.userAuthEmail = null
    state.isLoggedIn = false
    state.isAdmin = false
    state.isAuthenticating = true
    state.isSigningOut = false
    state.isAnonymous = null
    state.updateMessagesUnsubscribe = null
    state.messageRooms = []
    state.updateMessageRoomsSnapUnsubscribe = null
    state.joinedMessageRoomsUnsubscribers = {}
    state.messageRoomsCache = {}
    state.messageRoomsUnsubscribers = {}
    state.totalUnreadCount = 0
    state.infoUnreadCount = 0
    state.latestMessageRoomUnsubscribe = null
  },
  updatePermission(state, value) {
    state.isAdmin = value === 'admin'
    state.permission = value
  },
  updateSubscriptionEnder(state, value) {
    state.subscriptionEnder = value
  },
  setUpdateOrdersUnsubscribe(state, value) {
    state.updateOrdersUnsubscribe = value
  },
  setUpdateMessageRoomsUnsubscribe(state, value) {
    state.updateMessageRoomsUnsubscribe = value
  },
  setUpdateMessagesUnsubscribe(state, value) {
    state.updateMessagesUnsubscribe = value
  },
  updateCompanyId(state, value) {
    state.companyId = value
  },
  updateCurrentPoint(state, value) {
    state.currentPoint = value
  },
  updateAvailablePoint(state, value) {
    state.availablePoint = value
  },
  setUpdateCompanyDepositUnsubscribe(state, value) {
    state.updateCompanyDepositUnsubscribe = value
  },
  updateMessageRooms(state, value) {
    state.messageRooms = value
    // unReadCountの合計を計算
    state.totalUnreadCount = value.reduce((total, room) => {
      return total + (room.unReadCount || 0)
    }, 0)
  },
  updateInfoUnreadCount(state, value) {
    state.infoUnreadCount = value || 0
  },
  setUpdateMessageRoomsSnapUnsubscribe(state, value) {
    state.updateMessageRoomsSnapUnsubscribe = value
  },
  updateJoinedMessageRoomsUnsubscribers(state, { roomId, unsubscribe }) {
    if (unsubscribe) {
      state.joinedMessageRoomsUnsubscribers[roomId] = unsubscribe
    } else {
      delete state.joinedMessageRoomsUnsubscribers[roomId]
    }
  },
  updateMessageRoomsCache(state, { roomId, data }) {
    if (data) {
      state.messageRoomsCache[roomId] = data
    } else {
      delete state.messageRoomsCache[roomId]
    }
  },
  updateMessageRoomsUnsubscribers(state, { roomId, unsubscribe }) {
    if (!state.messageRoomsUnsubscribers) {
      state.messageRoomsUnsubscribers = {}
    }
    if (unsubscribe) {
      state.messageRoomsUnsubscribers[roomId] = unsubscribe
    } else {
      delete state.messageRoomsUnsubscribers[roomId]
    }
  },
  setLatestMessageRoomUnsubscribe(state, value) {
    state.latestMessageRoomUnsubscribe = value
  }
}

export const actions = {
  setupAuthStateHandler({ commit, state, dispatch }) {
    const shouldSetupAuthstate =
      !state.isLoggedIn && state.isAuthenticating && !state.isSigningOut
    if (shouldSetupAuthstate) {
      const ender = onAuthStateChanged(auth, async (user) => {
        commit('updateAuthStatus', user)

        // ユーザーがログインし、companyIdが設定された場合、メッセージルームの監視を開始
        if (user && user.uid) {
          const result = await auth.currentUser.getIdTokenResult(false)
          const claims = result.claims ? result.claims : null
          if (claims && claims.businessCompanyId) {
            dispatch('subscribeToMessageRooms')
          }
        }
      })

      commit('updateSubscriptionEnder', ender)
    }
  },
  endSubscription({ state, commit }) {
    if (state.subscriptionEnder) {
      state.subscriptionEnder()
      commit('updateSubscriptionEnder', null)
    }
  },
  async signOut({ state, commit, dispatch }) {
    commit('updateIsSigningOut', true)
    try {
      dispatch('endSubscription')
      if (state.updateWorksUnsubscribe) state.updateWorksUnsubscribe()
      if (state.updateCardsUnsubscribe) state.updateCardsUnsubscribe()
      if (state.updateSumUnsubscribe) state.updateSumUnsubscribe()
      if (state.updateOrdersUnsubscribe) state.updateOrdersUnsubscribe()
      if (state.updateMessageRoomsUnsubscribe)
        state.updateMessagesRoomsUnsubscribe()
      if (state.updateMessagesUnsubscribe) state.updateMessagesUnsubscribe()
      if (state.updateCompanyDepositUnsubscribe)
        state.updateCompanyDepositUnsubscribe()
      if (state.updateMessageRoomsSnapUnsubscribe)
        state.updateMessageRoomsSnapUnsubscribe()

      // joinedMessageRoomの監視を停止
      if (state.joinedMessageRoomsUnsubscribers) {
        Object.values(state.joinedMessageRoomsUnsubscribers).forEach(
          (unsubscribe) => {
            if (unsubscribe && typeof unsubscribe === 'function') {
              unsubscribe()
            }
          }
        )
      }

      // 個別メッセージルームの監視を停止
      if (state.messageRoomsUnsubscribers) {
        Object.values(state.messageRoomsUnsubscribers).forEach(
          (unsubscribe) => {
            if (unsubscribe && typeof unsubscribe === 'function') {
              unsubscribe()
            }
          }
        )
      }

      await auth.signOut()

      // 最新メッセージルームの監視を停止
      if (state.latestMessageRoomUnsubscribe) {
        state.latestMessageRoomUnsubscribe()
        commit('setLatestMessageRoomUnsubscribe', null)
      }

      commit('resetState')
    } catch (err) {
      console.error('error signing out of firebase', err)
    } finally {
      commit('updateIsSigningOut', false)
    }
  },
  setUnsubscribeUpdateOrders({ commit }, value) {
    commit('setUpdateOrdersUnsubscribe', value)
  },
  setUnsubscribeUpdateMessageRooms({ commit }, value) {
    commit('setUpdateMessageRoomsUnsubscribe', value)
  },
  setUnsubscribeUpdateMessages({ commit }, value) {
    commit('setUpdateMessagesUnsubscribe', value)
  },
  subscribeToCompanyDeposit({ commit, state }) {
    if (!state.companyId) {
      return
    }

    const unsubscribe = businessDB
      .businessCompanySecretCollection(state.companyId)
      .subscribeCompanyDeposit((depositData) => {
        if (!depositData) return
        const currentPoint = depositData.amount
        const availablePoint = depositData.availableAmount

        commit('updateCurrentPoint', currentPoint)
        commit('updateAvailablePoint', availablePoint)
      })

    // Vuex stateにunsubscribe関数を保存
    commit('setUpdateCompanyDepositUnsubscribe', unsubscribe)
  },
  // Firebaseのリスナーを解除する
  unsubscribeFromCompanyDeposit({ state, commit }) {
    if (state.updateCompanyDepositUnsubscribe) {
      state.updateCompanyDepositUnsubscribe()
      commit('setUpdateCompanyDepositUnsubscribe', null)
    }
  },
  updateMessageRooms({ commit }, value) {
    commit('updateMessageRooms', value)
  },
  setUpdateMessageRoomsSnapUnsubscribe({ commit }, value) {
    commit('setUpdateMessageRoomsSnapUnsubscribe', value)
  },
  // メッセージルームのリアルタイム監視を開始
  async subscribeToMessageRooms({ commit, state, dispatch }) {
    if (!state.companyId) {
      return
    }

    // updatedAt順（降順）に100件のみ取得
    const initialMessageRooms = await businessDB
      .messageRoomCollection(state.companyId)
      .getOpenRoomsWithLimit(100)

    if (!initialMessageRooms || initialMessageRooms.length === 0) {
      commit('updateMessageRooms', [])
      return
    }

    // 初期データを設定
    await dispatch('processInitialMessageRooms', initialMessageRooms)

    // 各メッセージルームを個別に監視
    for (const room of initialMessageRooms) {
      if (room && room.messageRoomId) {
        await dispatch('subscribeToIndividualMessageRoom', room.messageRoomId)
      }
    }

    // 最新メッセージルームの監視を開始
    await dispatch('startLatestMessageRoomMonitoring')
  },
  // 初期メッセージルームを処理
  async processInitialMessageRooms({ commit, state }, messageRooms) {
    // messageRoomIdが存在するものだけにフィルタ
    const validRooms = messageRooms.filter((room) => room && room.messageRoomId)
    const processedRooms = []
    const batchSize = 1000

    for (let i = 0; i < validRooms.length; i += batchSize) {
      const batch = validRooms.slice(i, i + batchSize)
      const batchPromises = batch.map(async (room) => {
        try {
          // joinedMessageRoom情報を取得
          const joinedMessageRoom = await businessDB
            .joinedMessageRoomCollection(state.companyId)
            .findById(room.messageRoomId)

          if (joinedMessageRoom) {
            room.unReadCount = joinedMessageRoom.unReadCount || 0
          }

          return room
        } catch (error) {
          console.error(
            `Error processing initial room ${room.messageRoomId}:`,
            error
          )
          return room
        }
      })
      const batchResults = await Promise.all(batchPromises)
      processedRooms.push(...batchResults)
    }

    commit('updateMessageRooms', processedRooms)
  },
  // 個別のメッセージルームを監視
  async subscribeToIndividualMessageRoom({ commit, state, dispatch }, roomId) {
    // 既存の監視を停止
    const existingUnsubscribe = state.messageRoomsUnsubscribers?.[roomId]
    if (existingUnsubscribe) {
      existingUnsubscribe()
    }

    // 個別のメッセージルームを監視
    const unsubscribe = businessDB
      .messageRoomCollection(state.companyId)
      .onSnapshotDoc(roomId, async (updatedRoom) => {
        if (updatedRoom && updatedRoom.messageRoomId) {
          // 変更されたルームのみを処理
          await dispatch('processIndividualMessageRoomUpdate', updatedRoom)
        } else {
          // ルームが削除された場合
          await dispatch('processIndividualMessageRoomDelete', roomId)
        }
      })

    // 監視を保存
    commit('updateMessageRoomsUnsubscribers', { roomId, unsubscribe })

    // joinedMessageRoomの監視を開始（初期データ取得後）
    await dispatch('subscribeToJoinedMessageRoom', roomId)
  },
  // 個別メッセージルームの更新を処理
  async processIndividualMessageRoomUpdate({ commit, state }, updatedRoom) {
    // nullチェック
    if (!updatedRoom || !updatedRoom.messageRoomId) {
      console.warn('Invalid updatedRoom data:', updatedRoom)
      return
    }

    const currentRooms = state.messageRooms
    const existingRoomIndex = currentRooms.findIndex(
      (r) => r.messageRoomId === updatedRoom.messageRoomId
    )

    // joinedMessageRoomの情報はリアルタイム監視で取得されるため、
    // ここでは重複してfindByIdで取得しない
    // unReadCountは既存の値を保持するか、リアルタイム監視で更新される

    const updatedRooms = [...currentRooms]
    if (existingRoomIndex === -1) {
      // 新しいルーム
      updatedRooms.push(updatedRoom)
    } else {
      // 既存ルームの更新（unReadCountは既存の値を保持）
      const existingRoom = currentRooms[existingRoomIndex]
      updatedRooms[existingRoomIndex] = {
        ...updatedRoom,
        unReadCount: existingRoom.unReadCount || 0
      }
    }

    commit('updateMessageRooms', updatedRooms)
  },
  // 個別メッセージルームの削除を処理
  async processIndividualMessageRoomDelete({ commit, state }, roomId) {
    const currentRooms = state.messageRooms
    const existingRoomIndex = currentRooms.findIndex(
      (r) => r.messageRoomId === roomId
    )

    if (existingRoomIndex !== -1) {
      // joinedMessageRoomの監視を停止
      const existingJoinedUnsubscribe =
        state.joinedMessageRoomsUnsubscribers[roomId]
      if (existingJoinedUnsubscribe) {
        existingJoinedUnsubscribe()
        commit('updateJoinedMessageRoomsUnsubscribers', {
          roomId,
          unsubscribe: null
        })
      }

      // 個別メッセージルームの監視を停止
      const existingRoomUnsubscribe = state.messageRoomsUnsubscribers[roomId]
      if (existingRoomUnsubscribe) {
        existingRoomUnsubscribe()
        commit('updateMessageRoomsUnsubscribers', {
          roomId,
          unsubscribe: null
        })
      }

      // ルームを削除
      const updatedRooms = [...currentRooms]
      updatedRooms.splice(existingRoomIndex, 1)
      commit('updateMessageRooms', updatedRooms)
    }
  },
  // 特定のjoinedMessageRoomを監視
  subscribeToJoinedMessageRoom({ commit, state }, roomId) {
    // 既存の監視を停止
    const existingUnsubscribe = state.joinedMessageRoomsUnsubscribers[roomId]
    if (existingUnsubscribe) {
      existingUnsubscribe()
    }

    // 新しい監視を開始
    const unsubscribe = businessDB
      .joinedMessageRoomCollection(state.companyId)
      .onUpdated(roomId, (joinedRoom) => {
        if (joinedRoom) {
          // メッセージルームの未読数を更新
          const currentRooms = state.messageRooms
          const roomIndex = currentRooms.findIndex(
            (r) => r.messageRoomId === roomId
          )

          if (roomIndex !== -1) {
            const updatedRooms = [...currentRooms]
            updatedRooms[roomIndex] = {
              ...updatedRooms[roomIndex],
              unReadCount: joinedRoom.unReadCount || 0
            }

            commit('updateMessageRooms', updatedRooms)
          }
        }
      })

    commit('updateJoinedMessageRoomsUnsubscribers', { roomId, unsubscribe })
  },
  // メッセージルームのリアルタイム監視を停止
  unsubscribeFromMessageRooms({ state, commit }) {
    if (state.updateMessageRoomsSnapUnsubscribe) {
      state.updateMessageRoomsSnapUnsubscribe()
      commit('setUpdateMessageRoomsSnapUnsubscribe', null)
    }
  },
  // companyIdが変更されたときにメッセージルームを再取得
  updateCompanyIdAndSubscribe({ commit, state, dispatch }, value) {
    // 既存の監視を停止
    if (state.updateMessageRoomsSnapUnsubscribe) {
      state.updateMessageRoomsSnapUnsubscribe()
      commit('setUpdateMessageRoomsSnapUnsubscribe', null)
    }

    // 最新メッセージルームの監視を停止
    if (state.latestMessageRoomUnsubscribe) {
      state.latestMessageRoomUnsubscribe()
      commit('setLatestMessageRoomUnsubscribe', null)
    }

    // companyIdを更新
    commit('updateCompanyId', value)

    // 新しいcompanyIdでメッセージルームの監視を開始
    if (state.isLoggedIn && value) {
      dispatch('subscribeToMessageRooms')
    }
  },
  // 最新メッセージルームの監視を開始
  async startLatestMessageRoomMonitoring({ commit, state, dispatch }) {
    if (!state.companyId) {
      return
    }

    // 既存の監視を停止
    if (state.latestMessageRoomUnsubscribe) {
      state.latestMessageRoomUnsubscribe()
    }

    // 最新1件のメッセージルームを監視
    const unsubscribe = businessDB
      .messageRoomCollection(state.companyId)
      .onSnapshotWithQuery(
        [{ field: 'status', operator: '==', value: 'open' }],
        [{ field: 'updatedAt', direction: 'desc' }],
        1,
        async (rooms, snapshot) => {
          if (!rooms || rooms.length === 0) return

          const changes = snapshot.docChanges()

          changes.forEach(async (change) => {
            const room = businessDB
              .messageRoomCollection(state.companyId)
              .getDataFromDocumentData(change.doc)

            if (!room || !room.messageRoomId) return

            if (change.type === 'added') {
              // 新規メッセージルームを検出
              const existingIndex = state.messageRooms.findIndex(
                (r) => r.messageRoomId === room.messageRoomId
              )

              if (existingIndex === -1) {
                // 既存にない場合のみ追加
                await dispatch('processNewMessageRoom', room)
              }
            }
          })
        }
      )

    commit('setLatestMessageRoomUnsubscribe', unsubscribe)
  },
  // 新規メッセージルームを処理
  async processNewMessageRoom({ commit, state, dispatch }, room) {
    try {
      // joinedMessageRoom情報を取得
      const joinedMessageRoom = await businessDB
        .joinedMessageRoomCollection(state.companyId)
        .findById(room.messageRoomId)

      if (joinedMessageRoom) {
        room.unReadCount = joinedMessageRoom.unReadCount || 0
      }

      // 既存のルームに追加（updatedAt順でソート）
      const updatedRooms = [...state.messageRooms]
      updatedRooms.push(room)
      updatedRooms.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0)
        const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0)
        return bTime - aTime
      })

      commit('updateMessageRooms', updatedRooms)

      // 個別監視を開始
      await dispatch('subscribeToIndividualMessageRoom', room.messageRoomId)
    } catch (error) {
      console.error(`Error processing new room ${room.messageRoomId}:`, error)
    }
  }
}

async function saveSignInTime(user) {
  try {
    await businessDB.businessUserCollection().update(user.uid, {
      lastSignInTime: new Date(),
      updatedAt: new Date()
    })
  } catch (error) {
    console.log(error)
  }
}
